/**
 * The TraineePreferences-Component lets trainees give preferences on their mentor, ip and ressort choice
 */
import { Button, Divider, Grid, MenuItem, Paper, TextField, Typography, useTheme } from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { AuthContext } from "../../context/auth-context/AuthContext";
import useDepartments from "../../hooks/members/useDepartments";
import useCurrentTraineeGeneration from "../../hooks/trainees/useCurrentTraineeGeneration";
import useTraineeGeneration from "../../hooks/trainees/useTraineeGeneration";
import useTraineePreferences from "../../hooks/trainees/useTraineePreferences";
import { TraineeChoiceDto } from "../../types/traineesTypes";
import { showErrorMessage } from "../../utils/toastUtils";
import dayjs from "dayjs";

/**
 * Options to create a new member and to change the status of members
 */
const TraineePreferences: React.FunctionComponent = () => {
  const theme = useTheme();

  const styles = {
    // Header text of a paper marking a section of a page
    paperHeaderText: {
      marginLeft: theme.spacing(1),
      marginTop: theme.spacing(1),
    },
    paperText: {
      marginLeft: theme.spacing(1),
      marginTop: theme.spacing(1),
    },
    // Header divider of a paper marking a section of a page
    paperHeaderDivider: {
      marginLeft: theme.spacing(1),
      marginRight: theme.spacing(1),
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },
    paperContainer: {
      marginBottom: "10px",
    },
    inputContainer: {
      padding: theme.spacing(1),
    },
    selectionElement: {
      [theme.breakpoints.up("md")]: {
        margin: theme.spacing(1),
        width: "155px",
      },
      [theme.breakpoints.down("md")]: {
        margin: theme.spacing(1),
        width: "120px",
      },
      [theme.breakpoints.down("sm")]: {
        margin: theme.spacing(1),
        width: "120px",
      },
    },
    motivationalText: {
      marginLeft: theme.spacing(1),
      marginRight: theme.spacing(1),
      width: "95%",
    },
    inputButton: {
      margin: theme.spacing(1),
    },
  };

  const { auth } = useContext(AuthContext);
  const { currentTraineeGeneration } = useCurrentTraineeGeneration();
  const { internalProjectsOfGeneration, mentorsOfGeneration, updatePreferences } = useTraineeGeneration(
    auth.generationId || 0
  );
  const { traineePreferences } = useTraineePreferences(auth?.userID);
  const { departments } = useDepartments();
  const filteredDepartments = departments.filter((department) => department.departmentId !== 8);

  const [traineeChoicesFormState, setTraineeChoicesFormState] = useState<TraineeChoiceDto | null>(traineePreferences);

  useEffect(() => {
    if (traineePreferences) {
      setTraineeChoicesFormState(traineePreferences);
    }
  }, [traineePreferences]);

  /**
   * Handles the change event on the first department preference
   * @param event
   */
  const handleRessortFirstChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const newValue = parseInt(event.target.value);
    const department = filteredDepartments.find((department) => department.departmentId === newValue);
    if (!department?.shortName) {
      showErrorMessage("Ungültige Präferenz");
      return;
    }
    const newShortName = department.shortName;

    if (traineeChoicesFormState) {
      if (
        traineeChoicesFormState.departmentChoice2 === newValue ||
        traineeChoicesFormState.departmentChoice3 === newValue
      ) {
        showErrorMessage("Für jede Präferenz muss eine andere Wahl getroffen werden");
        return;
      } else {
        const newPrefs: TraineeChoiceDto = {
          ...traineeChoicesFormState,
          departmentChoice1: newValue,
          departmentChoice1ShortName: newShortName,
        };
        setTraineeChoicesFormState(newPrefs);
      }
    }
  };

  /**
   * Handles the change event on the second department preference
   * @param event
   */
  const handleRessortSecondChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const newValue = parseInt(event.target.value);
    const department = filteredDepartments.find((department) => department.departmentId === newValue);
    if (!department?.shortName) {
      showErrorMessage("Ungültige Präferenz");
      return;
    }
    const newKuerzel = department.shortName;

    if (traineeChoicesFormState) {
      if (
        traineeChoicesFormState.departmentChoice1 === newValue ||
        traineeChoicesFormState.departmentChoice3 === newValue
      ) {
        showErrorMessage("Für jede Präferenz muss eine andere Wahl getroffen werden");
      } else {
        setTraineeChoicesFormState({
          ...traineeChoicesFormState,
          departmentChoice2: newValue,
          departmentChoice2ShortName: newKuerzel,
        });
      }
    }
  };

  /**
   * Handles the change event on the second department preference
   * @param event
   */
  const handleRessortThirdChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const newValue = parseInt(event.target.value);
    const department = filteredDepartments.find((department) => department.departmentId === newValue);
    if (!department?.shortName) {
      showErrorMessage("Ungültige Präferenz");
      return;
    }
    const newKuerzel = department.shortName;

    if (traineeChoicesFormState) {
      if (
        traineeChoicesFormState.departmentChoice1 === newValue ||
        traineeChoicesFormState.departmentChoice2 === newValue
      ) {
        showErrorMessage("Für jede Präferenz muss eine andere Wahl getroffen werden");
      } else {
        setTraineeChoicesFormState({
          ...traineeChoicesFormState,
          departmentChoice3: newValue,
          departmentChoice3ShortName: newKuerzel,
        });
      }
    }
  };

  /**
   * Handles the change event on the first internal project preference
   * @param event
   */
  const handleIPFirstChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const newValue = parseInt(event.target.value);
    const internalProject = internalProjectsOfGeneration.find(
      (internalProject) => internalProject.internalProjectId === newValue
    );
    if (!internalProject?.abbreviation) {
      showErrorMessage("Ungültiges Internes Projekt");
      return;
    }
    const newKuerzel = internalProject.abbreviation;

    if (traineeChoicesFormState) {
      if (
        traineeChoicesFormState.internalProjectChoice2 === newValue ||
        traineeChoicesFormState.internalProjectChoice3 === newValue
      ) {
        showErrorMessage("Für jede Präferenz muss eine andere Wahl getroffen werden");
      } else {
        setTraineeChoicesFormState({
          ...traineeChoicesFormState,
          internalProjectChoice1: newValue,
          internalProjectChoice1ShortName: newKuerzel,
        });
      }
    }
  };

  /**
   * Handles the change event on the second internal project preference
   * @param event
   */
  const handleIPSecondChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const newValue = parseInt(event.target.value);
    const internalProject = internalProjectsOfGeneration.find(
      (internalProject) => internalProject.internalProjectId === newValue
    );
    if (!internalProject?.abbreviation) {
      showErrorMessage("Ungültiges Internes Projekt");
      return;
    }
    const newKuerzel = internalProject.abbreviation;

    if (traineeChoicesFormState) {
      if (
        traineeChoicesFormState.internalProjectChoice1 === newValue ||
        traineeChoicesFormState.internalProjectChoice3 === newValue
      ) {
        showErrorMessage("Für jede Präferenz muss eine andere Wahl getroffen werden");
      } else {
        setTraineeChoicesFormState({
          ...traineeChoicesFormState,
          internalProjectChoice2: newValue,
          internalProjectChoice2ShortName: newKuerzel,
        });
      }
    }
  };

  /**
   * Handles the change event on the third internal project preference
   * @param event
   */
  const handleIPThirdChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const newValue = parseInt(event.target.value);
    const internalProject = internalProjectsOfGeneration.find(
      (internalProject) => internalProject.internalProjectId === newValue
    );
    if (!internalProject?.abbreviation) {
      showErrorMessage("Ungültiges Internes Projekt");
      return;
    }
    const newKuerzel = internalProject.abbreviation;

    if (traineeChoicesFormState) {
      if (
        traineeChoicesFormState.internalProjectChoice1 === newValue ||
        traineeChoicesFormState.internalProjectChoice2 === newValue
      ) {
        showErrorMessage("Für jede Präferenz muss eine andere Wahl getroffen werden");
      } else {
        setTraineeChoicesFormState({
          ...traineeChoicesFormState,
          internalProjectChoice3: newValue,
          internalProjectChoice3ShortName: newKuerzel,
        });
      }
    }
  };

  /**
   * Handles the change event on the first department preference
   * @param event
   */
  const handleMentorFirstChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const newValue = parseInt(event.target.value);
    const mentor = mentorsOfGeneration.find((mentor) => mentor.memberId === newValue);
    if (!mentor?.firstname) {
      showErrorMessage("Ungültiger Mentor");
      return;
    }
    const newName = mentor.firstname.charAt(0) + "." + mentor.lastname;

    if (traineeChoicesFormState) {
      if (traineeChoicesFormState.mentorChoice2 === newValue || traineeChoicesFormState.mentorChoice3 === newValue) {
        showErrorMessage("Für jede Präferenz muss eine andere Wahl getroffen werden");
      } else {
        setTraineeChoicesFormState({ ...traineeChoicesFormState, mentorChoice1: newValue, mentorChoice1Name: newName });
      }
    }
  };

  /**
   * Handles the change event on the second department preference
   * @param event
   */
  const handleMentorSecondChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const newValue = parseInt(event.target.value);
    const mentor = mentorsOfGeneration.find((mentor) => mentor.memberId === newValue);
    if (!mentor?.firstname) {
      showErrorMessage("Ungültiger Mentor");
      return;
    }
    const newName = mentor.firstname.charAt(0) + "." + mentor.lastname;

    if (traineeChoicesFormState) {
      if (traineeChoicesFormState.mentorChoice1 === newValue || traineeChoicesFormState.mentorChoice3 === newValue) {
        showErrorMessage("Für jede Präferenz muss eine andere Wahl getroffen werden");
      } else {
        setTraineeChoicesFormState({ ...traineeChoicesFormState, mentorChoice2: newValue, mentorChoice2Name: newName });
      }
    }
  };

  /**
   * Handles the change event on the second department preference
   * @param event
   */
  const handleMentorThirdChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const newValue = parseInt(event.target.value);
    const mentor = mentorsOfGeneration.find((mentor) => mentor.memberId === newValue);
    if (!mentor?.firstname) {
      showErrorMessage("Ungültiger Mentor");
      return;
    }
    const newName = mentor.firstname.charAt(0) + "." + mentor.lastname;

    if (traineeChoicesFormState) {
      if (traineeChoicesFormState.mentorChoice1 === newValue || traineeChoicesFormState.mentorChoice2 === newValue) {
        showErrorMessage("Für jede Präferenz muss eine andere Wahl getroffen werden");
      } else {
        setTraineeChoicesFormState({ ...traineeChoicesFormState, mentorChoice3: newValue, mentorChoice3Name: newName });
      }
    }
  };

  /**
   * Handles the change event on the first internal project motviational text
   * @param event
   */
  const handleFirstIPMotivationChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (traineeChoicesFormState) {
      setTraineeChoicesFormState({ ...traineeChoicesFormState, internalProjectChoice1Motivation: event.target.value });
    }
  };

  /**
   * Handles the change event on the first internal project motviational text
   * @param event
   */
  const handleSecondIPMotivationChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (traineeChoicesFormState) {
      setTraineeChoicesFormState({ ...traineeChoicesFormState, internalProjectChoice2Motivation: event.target.value });
    }
  };

  /**
   * Handles the change event on the first internal project motviational text
   * @param event
   */
  const handleThirdIPMotivationChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (traineeChoicesFormState) {
      setTraineeChoicesFormState({ ...traineeChoicesFormState, internalProjectChoice3Motivation: event.target.value });
    }
  };

  const savePreferences = async () => {
    if (!auth.userID || !traineeChoicesFormState) {
      showErrorMessage("Ungültige Präferenzen");
      return;
    }
    await updatePreferences({ memberId: auth.userID, preferences: traineeChoicesFormState });
  };

  /**
   * Renders the different selection possibilities for the department
   * @returns A map of jsx elements
   */
  const renderRessortItems = () => {
    return filteredDepartments.map((department) => {
      return (
        <MenuItem key={department.departmentId} value={department.departmentId}>
          {department.shortName}
        </MenuItem>
      );
    });
  };

  /**
   * Render the ressort selection jsx element
   * @returns A jsx element
   */
  const renderRessortSelection = () => {
    return (
      <div>
        <Typography variant="h5" sx={styles.paperHeaderText}>
          Ressort
        </Typography>
        <TextField
          label="1. Präferenz"
          color="primary"
          sx={styles.selectionElement}
          onChange={handleRessortFirstChange}
          value={traineeChoicesFormState?.departmentChoice1 || ""}
          select
        >
          {renderRessortItems()}
        </TextField>
        <TextField
          label="2. Präferenz"
          color="primary"
          sx={styles.selectionElement}
          onChange={handleRessortSecondChange}
          value={traineeChoicesFormState?.departmentChoice2 || ""}
          select
        >
          {renderRessortItems()}
        </TextField>
        <TextField
          label="3. Präferenz"
          color="primary"
          sx={styles.selectionElement}
          onChange={handleRessortThirdChange}
          value={traineeChoicesFormState?.departmentChoice3 || ""}
          select
        >
          {renderRessortItems()}
        </TextField>
      </div>
    );
  };

  /**
   * Renders the different selection possibilities for the mentor
   * @returns A map of jsx elements
   */
  const renderMentorItems = () => {
    return mentorsOfGeneration.map((mentor) => {
      if (mentor.memberId) {
        return (
          <MenuItem key={mentor.memberId} value={mentor.memberId}>
            {mentor.firstname + " " + mentor.lastname}
          </MenuItem>
        );
      }
    });
  };

  /**
   * Render the mentor selection jsx element
   * @returns A jsx element
   */
  const renderMentorSelection = () => {
    return (
      <div>
        <Typography variant="h5" sx={styles.paperHeaderText}>
          Mentor
        </Typography>
        <TextField
          label="1. Präferenz"
          color="primary"
          sx={styles.selectionElement}
          onChange={handleMentorFirstChange}
          value={traineeChoicesFormState?.mentorChoice1 || ""}
          select
        >
          {renderMentorItems()}
        </TextField>
        <TextField
          label="2. Präferenz"
          color="primary"
          sx={styles.selectionElement}
          onChange={handleMentorSecondChange}
          value={traineeChoicesFormState?.mentorChoice2 || ""}
          select
        >
          {renderMentorItems()}
        </TextField>
        <TextField
          label="3. Präferenz"
          color="primary"
          sx={styles.selectionElement}
          onChange={handleMentorThirdChange}
          value={traineeChoicesFormState?.mentorChoice3 || ""}
          select
        >
          {renderMentorItems()}
        </TextField>
      </div>
    );
  };

  /**
   * Renders the different selection possibilities for the internal project
   * @returns A map of jsx elements
   */
  const renderIPItems = () => {
    return internalProjectsOfGeneration.map((internalproject) => {
      return (
        <MenuItem key={internalproject.internalProjectId} value={internalproject.internalProjectId}>
          {internalproject.projectName}
        </MenuItem>
      );
    });
  };

  /**
   * Render the internal project selection jsx element
   * @returns A jsx element
   */
  const renderIPSelection = () => {
    return (
      <div>
        <Typography variant="h5" sx={styles.paperHeaderText}>
          Internes Projekt
        </Typography>
        <Grid item xs={12}>
          <TextField
            label="1. Präferenz"
            color="primary"
            sx={styles.selectionElement}
            onChange={handleIPFirstChange}
            value={traineeChoicesFormState?.internalProjectChoice1 || ""}
            select
          >
            {renderIPItems()}
          </TextField>
        </Grid>
        <Grid item xs={12} width={"100%"}>
          <TextField
            label="Motivationstext 1. Präferenz"
            color="primary"
            sx={styles.motivationalText}
            multiline
            rows={10}
            onChange={handleFirstIPMotivationChange}
            value={traineeChoicesFormState?.internalProjectChoice1Motivation}
          ></TextField>
        </Grid>
        <Divider sx={styles.paperHeaderDivider} />
        <Grid item xs={12}>
          <TextField
            label="2. Präferenz"
            color="primary"
            sx={styles.selectionElement}
            onChange={handleIPSecondChange}
            value={traineeChoicesFormState?.internalProjectChoice2 || ""}
            select
          >
            {renderIPItems()}
          </TextField>
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Motivationstext 2. Präferenz"
            color="primary"
            sx={styles.motivationalText}
            fullWidth
            multiline
            rows={10}
            onChange={handleSecondIPMotivationChange}
            value={traineeChoicesFormState?.internalProjectChoice2Motivation}
          ></TextField>
        </Grid>
        <Divider sx={styles.paperHeaderDivider} />
        <Grid item xs={12}>
          <TextField
            label="3. Präferenz"
            color="primary"
            sx={styles.selectionElement}
            onChange={handleIPThirdChange}
            value={traineeChoicesFormState?.internalProjectChoice3 || ""}
            select
          >
            {renderIPItems()}
          </TextField>
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Motivationstext 3. Präferenz"
            color="primary"
            sx={styles.motivationalText}
            fullWidth
            multiline
            rows={10}
            onChange={handleThirdIPMotivationChange}
            value={traineeChoicesFormState?.internalProjectChoice3Motivation}
          ></TextField>
        </Grid>
        <Divider sx={styles.paperHeaderDivider} />
      </div>
    );
  };

  const renderContent = () => {
    if (!currentTraineeGeneration || !traineeChoicesFormState) {
      return null;
    }
    const currentDate = new Date();
    const startDate = currentTraineeGeneration?.electionStart;
    const endDate = currentTraineeGeneration?.electionEnd;

    const isWithinDateRange =
      startDate && endDate && currentDate >= new Date(startDate) && currentDate <= new Date(endDate);
    const userIsPartOfGeneration = auth.generationId === currentTraineeGeneration.generationId;

    if (isWithinDateRange && userIsPartOfGeneration) {
      return (
        <Grid container>
          <Grid item xs={12}>
            <Typography variant="h5" sx={styles.paperHeaderText}>
              Trainee Wahl
            </Typography>
            <Typography sx={styles.paperText}>
              Hier kannst du Präferenzen für dein Wunsch Ressort, Mentor und Internes Projekt abgeben.<br></br>
              Bitte bedenke, dass du deine Präferenzen bis spätestens{" "}
              <b>
                {currentTraineeGeneration.electionEnd
                  ? dayjs(currentTraineeGeneration.electionEnd).format("DD.MM.YYYY HH:mm")
                  : "t.b.d."}
              </b>{" "}
              anpassen kannst.
            </Typography>
            <Divider sx={styles.paperHeaderDivider} />
            {renderRessortSelection()}
            <Divider sx={styles.paperHeaderDivider} />
            {renderMentorSelection()}
            <Divider sx={styles.paperHeaderDivider} />
            {renderIPSelection()}
            <Button variant="contained" color="primary" sx={styles.inputButton} onClick={savePreferences}>
              Präferenzen Speichern
            </Button>
          </Grid>
        </Grid>
      );
    }
    if (!isWithinDateRange) {
      return (
        <Grid container>
          <Grid item xs={12}>
            <Typography variant="h5" sx={styles.paperHeaderText}>
              Trainee-Zuteilung {currentTraineeGeneration.description}
            </Typography>
            <Typography sx={styles.paperText}>
              Die Präferenzwahlen sind noch nicht geöffnet. Bitte warte, bis die Wahlperiode beginnt.
            </Typography>
          </Grid>
        </Grid>
      );
    }
    if (!userIsPartOfGeneration) {
      return (
        <Grid container>
          <Grid item xs={12}>
            <Typography variant="h5" sx={styles.paperHeaderText}>
              Trainee Wahl
            </Typography>
            <Typography sx={styles.paperText}>
              Du bist nicht Teil der aktuellen Generation und kannst daher keine Präferenzen abgeben.
            </Typography>
          </Grid>
        </Grid>
      );
    }

    return null;
  };

  return (
    <div>
      <Paper sx={styles.paperContainer}>{renderContent()}</Paper>
    </div>
  );
};

export default TraineePreferences;
